import React, { useEffect, useMemo, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Store, Users, TrendingUp, Coffee, Map, Check, Info, MessageSquare, Mail, Calendar, MapPin, ShoppingBag, ChevronRight, Truck, Maximize2, X } from 'lucide-react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { ExpositorRegistrationModal } from '../components/ExpositorRegistrationModal';
import { formatCOP, priceBreakdown } from '../utils/format';
import { supabase } from '../utils/supabase';
type StandStatus = 'available' | 'reserved' | 'sold';
type PavilionType = 'comercial' | 'foodtrucks';
interface Stand {
  id: string;
  status: StandStatus;
  size: string;
  price: number;
  isCorner?: boolean;
  isLarge?: boolean;
  type: PavilionType;
  standType?: 'AAA' | 'AA' | 'A';
  frentes?: string;
  row?: number;
  col?: number;
}
// Stand type definitions for commercial pavilion (36 total)
const AAA_STANDS = [13, 24];
const AA_STANDS = [1, 6, 7, 12, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 25, 30, 31, 36];
// Toldos availability
const TOLDOS_TOTAL = 20;
// Cute food truck icon — proper truck with awning, serving window, and wheels
function FoodTruckIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 64 48" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden data-id="element-2020">
      {/* Striped awning */}
      <path d="M10 14 L54 14 L50 8 L14 8 Z" fill="currentColor" opacity="0.85" data-id="element-2021" />
      <path d="M18 8 L18 14 M26 8 L26 14 M34 8 L34 14 M42 8 L42 14 M50 8 L50 14" stroke="white" strokeWidth="0.8" opacity="0.4" data-id="element-2022" />

      {/* Truck body */}
      <rect x="6" y="14" width="44" height="22" rx="2" fill="currentColor" data-id="element-2023" />

      {/* Cab on the right */}
      <path d="M50 18 L58 22 L58 36 L50 36 Z" fill="currentColor" opacity="0.75" data-id="element-2024" />

      {/* Cab window */}
      <path d="M51 21 L56.5 23.5 L56.5 28 L51 28 Z" fill="white" opacity="0.45" data-id="element-2025" />

      {/* Serving window */}
      <rect x="12" y="18" width="22" height="12" rx="1" fill="white" opacity="0.85" data-id="element-2026" />

      {/* Counter */}
      <rect x="10" y="30" width="26" height="2" fill="white" opacity="0.55" data-id="element-2027" />

      {/* Wheels */}
      <circle cx="18" cy="38" r="4" fill="#1a1a1a" data-id="element-2028" />
      <circle cx="18" cy="38" r="1.6" fill="white" opacity="0.8" data-id="element-2029" />
      <circle cx="48" cy="38" r="4" fill="#1a1a1a" data-id="element-2030" />
      <circle cx="48" cy="38" r="1.6" fill="white" opacity="0.8" data-id="element-2031" />
    </svg>;
}
export function ExpositoresPage() {
  const [activeTab, setActiveTab] = useState<PavilionType>('comercial');
  const [ftSubTab, setFtSubTab] = useState<'foodtrucks' | 'toldos'>('foodtrucks');
  const [selectedStand, setSelectedStand] = useState<Stand | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [registrationType, setRegistrationType] = useState<'comercial' | 'foodtruck' | 'toldo'>('comercial');
  const [ftWidthStr, setFtWidthStr] = useState('5');
  const [ftLengthStr, setFtLengthStr] = useState('3');
  const [toldosQuantity, setToldosQuantity] = useState(0);
  const [occupiedStands, setOccupiedStands] = useState<Record<string, 'paid' | 'pending_payment'>>({});
  const [realToldosSold, setRealToldosSold] = useState(0);
  useEffect(() => {
    // Status synonyms — keep the front tolerant of different webhooks / dashboards
    // that write 'paid' OR 'approved' (Wompi's word) for the same outcome.
    const SOLD_STATUSES = ['paid', 'approved', 'complete', 'completed'];
    const RESERVED_STATUSES = ['pending_payment', 'pending'];
    const ALL_BLOCKING = [...SOLD_STATUSES, ...RESERVED_STATUSES];
    const normalize = (raw: string | null | undefined): 'paid' | 'pending_payment' | null => {
      if (!raw) return null;
      const s = raw.toLowerCase().trim();
      if (SOLD_STATUSES.includes(s)) return 'paid';
      if (RESERVED_STATUSES.includes(s)) return 'pending_payment';
      return null;
    };
    const fetchAvailability = async () => {
      // Fetch stands — case-insensitive match on any blocking status
      const {
        data: standsData
      } = await supabase.from('expositor_reservations').select('stand_id, status').not('stand_id', 'is', null);
      if (standsData) {
        const occupied: Record<string, 'paid' | 'pending_payment'> = {};
        standsData.forEach(row => {
          const normalized = normalize(row.status);
          if (row.stand_id && normalized) {
            occupied[row.stand_id] = normalized;
          }
        });
        setOccupiedStands(occupied);
      }
      // Fetch toldos
      const {
        data: toldosData
      } = await supabase.from('toldos_reservations').select('quantity, status');
      if (toldosData) {
        const sold = toldosData.reduce((sum, row) => {
          const normalized = normalize(row.status);
          return normalized ? sum + (row.quantity || 0) : sum;
        }, 0);
        setRealToldosSold(sold);
      }
    };
    fetchAvailability();
    // Realtime subscription — refresh whenever a reservation changes
    const standsChannel = supabase.channel('expositor_reservations_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'expositor_reservations'
    }, () => fetchAvailability()).subscribe();
    const toldosChannel = supabase.channel('toldos_reservations_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'toldos_reservations'
    }, () => fetchAvailability()).subscribe();
    return () => {
      supabase.removeChannel(standsChannel);
      supabase.removeChannel(toldosChannel);
    };
  }, []);
  const ftPricePerM2 = 150000; // Updated from 57000
  const ftWidth = parseFloat(ftWidthStr) || 0;
  const ftLength = parseFloat(ftLengthStr) || 0;
  const ftTotalM2 = ftWidth * ftLength;
  const ftTotalPrice = ftTotalM2 * ftPricePerM2;
  // Calculate available toldos with real data + fake floor until 40% (8) sold
  const displayedToldosSold = realToldosSold < 8 ? Math.max(6, realToldosSold) : realToldosSold;
  const toldosAvailable = Math.max(0, TOLDOS_TOTAL - displayedToldosSold);
  const toldosPrice = 300000;
  const toldosTotalPrice = toldosPrice * toldosQuantity;
  const {
    commercialStands,
    foodTruckStands
  } = useMemo(() => {
    const comm: Stand[] = [];
    // Create 36 commercial stands (01-36)
    for (let i = 1; i <= 36; i++) {
      let standType: 'AAA' | 'AA' | 'A';
      let price: number;
      let frentes: string;
      let isLarge: boolean;
      if (AAA_STANDS.includes(i)) {
        standType = 'AAA';
        price = 800000;
        frentes = 'Tres frentes';
        isLarge = true;
      } else if (AA_STANDS.includes(i)) {
        standType = 'AA';
        price = 600000;
        frentes = 'Dos frentes';
        isLarge = true;
      } else {
        standType = 'A';
        price = 500000;
        frentes = 'Un frente';
        isLarge = false;
      }
      const paddedNum = i.toString().padStart(2, '0');
      const id = `${standType}${paddedNum}`;
      const status: StandStatus = occupiedStands[id] === 'paid' ? 'sold' : occupiedStands[id] === 'pending_payment' ? 'reserved' : 'available';
      const row = i <= 12 ? 1 : i <= 24 ? 2 : 3;
      comm.push({
        id,
        status,
        size: '2x2m',
        price,
        type: 'comercial',
        standType,
        frentes,
        isLarge,
        row,
        col: i
      });
    }
    const foodTrucks: Stand[] = [];
    for (let i = 1; i <= 8; i++) {
      const id = `FT${i}`;
      const status: StandStatus = occupiedStands[id] === 'paid' ? 'sold' : occupiedStands[id] === 'pending_payment' ? 'reserved' : 'available';
      foodTrucks.push({
        id,
        status,
        size: 'Variable',
        price: ftPricePerM2,
        type: 'foodtrucks',
        row: i <= 4 ? 1 : 2,
        col: i <= 4 ? i : i - 4
      });
    }
    return {
      commercialStands: comm,
      foodTruckStands: foodTrucks
    };
  }, [ftPricePerM2, occupiedStands]);
  const currentStands = activeTab === 'comercial' ? commercialStands : foodTruckStands;
  const handleStandClick = (stand: Stand) => {
    if (stand.status === 'available') {
      setSelectedStand(stand);
      setIsModalOpen(false); // Close modal when stand selected to show detail panel
    }
  };
  const getCommercialStandClasses = (stand: Stand) => {
    const isSelected = selectedStand?.id === stand.id;
    // AAA stands (super premium) - gold styling
    if (stand.standType === 'AAA') {
      if (stand.status === 'sold') {
        return `rounded-lg border-[3px] flex flex-col items-center justify-center transition-all font-bold relative bg-red-100 border-red-400 text-red-700 cursor-not-allowed opacity-60`;
      }
      if (stand.status === 'reserved') {
        return `rounded-lg border-[3px] flex flex-col items-center justify-center transition-all font-bold relative bg-amber-100 border-amber-400 text-amber-700 cursor-not-allowed opacity-75`;
      }
      return `rounded-lg border-[4px] flex flex-col items-center justify-center transition-all font-bold relative bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-500 text-yellow-900 hover:from-yellow-100 hover:to-amber-100 hover:border-yellow-600 hover:shadow-xl hover:shadow-yellow-400/40 cursor-pointer
        ${isSelected ? 'ring-4 ring-yellow-500 ring-offset-2 scale-110 z-10 shadow-2xl' : ''}`;
    }
    // AA stands (premium) - amber styling
    if (stand.standType === 'AA') {
      if (stand.status === 'sold') {
        return `rounded-lg border-[3px] flex flex-col items-center justify-center transition-all font-bold relative bg-red-100 border-red-400 text-red-700 cursor-not-allowed opacity-60`;
      }
      if (stand.status === 'reserved') {
        return `rounded-lg border-[3px] flex flex-col items-center justify-center transition-all font-bold relative bg-amber-100 border-amber-400 text-amber-700 cursor-not-allowed opacity-75`;
      }
      return `rounded-lg border-[3px] flex flex-col items-center justify-center transition-all font-bold relative bg-white border-amber-400 text-amber-800 hover:bg-amber-50 hover:border-amber-500 hover:shadow-lg hover:shadow-amber-400/30 cursor-pointer
        ${isSelected ? 'ring-4 ring-amber-400 ring-offset-2 scale-110 z-10 shadow-xl' : ''}`;
    }
    // A stands (standard) - gray styling
    if (stand.status === 'sold') {
      return `aspect-square rounded border-2 flex items-center justify-center transition-all text-[9px] font-bold bg-red-100 border-red-400 text-red-700 cursor-not-allowed opacity-60`;
    }
    if (stand.status === 'reserved') {
      return `aspect-square rounded border-2 flex items-center justify-center transition-all text-[9px] font-bold bg-amber-100 border-amber-400 text-amber-700 cursor-not-allowed opacity-75`;
    }
    return `aspect-square rounded border-2 flex items-center justify-center transition-all text-[9px] font-bold bg-white border-gray-300 text-gray-700 hover:bg-green-50 hover:border-green-400 cursor-pointer
      ${isSelected ? 'ring-4 ring-brand-cyan ring-offset-2 scale-105' : ''}`;
  };
  const getFoodTruckStandClasses = (stand: Stand) => {
    const isSelected = selectedStand?.id === stand.id;
    const base = 'flex flex-col items-center justify-center transition-all py-3 px-2 rounded-xl';
    if (stand.status === 'sold') {
      return `${base} bg-red-500/20 border-2 border-red-500/40 text-red-300 cursor-not-allowed`;
    }
    if (stand.status === 'reserved') {
      return `${base} bg-red-500/15 border-2 border-red-500/30 text-red-200/80 cursor-not-allowed`;
    }
    return `${base} text-white hover:scale-110 hover:-translate-y-1 cursor-pointer
      ${isSelected ? 'scale-110 -translate-y-1 drop-shadow-[0_0_12px_rgba(249,115,22,0.8)]' : ''}`;
  };
  return <div className="pt-20 bg-brand-navy" data-id="element-2032">
      {/* Hero Banner */}
      <section className="relative py-24 bg-brand-navy overflow-hidden" data-id="element-2033">
        <div className="absolute inset-0" data-id="element-2034">
          <img src="https://images.unsplash.com/photo-1555244162-803834f70033?w=1920&q=80&auto=format&fit=crop" alt="Feria Bazar" className="w-full h-full object-cover opacity-30" data-id="element-2035" />
          <div className="absolute inset-0 bg-brand-navy/70" data-id="element-2036"></div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-2037">
          <Link to="/" className="inline-flex items-center gap-2 text-brand-cyan hover:text-white transition-colors mb-8 text-sm" data-id="element-2038">
            <ArrowLeft className="w-4 h-4" data-id="element-2039" /> Volver al inicio
          </Link>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-5xl md:text-7xl text-white mb-4 font-bold" data-id="element-2040">
            Feria Bazar <span className="text-brand-cyan" data-id="element-2041">Deportiva</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-xl text-white/80 max-w-2xl mx-auto mb-8" data-id="element-2042">
            El espacio perfecto para conectar tu marca con más de 2,000 amantes
            del deporte, el bienestar y las mascotas.
          </motion.p>
          <div className="flex flex-wrap justify-center gap-6 text-white/90" data-id="element-2043">
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-2044">
              <Calendar className="w-5 h-5 text-brand-cyan" data-id="element-2045" /> 26 Julio 2026
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-2046">
              <MapPin className="w-5 h-5 text-brand-green" data-id="element-2047" /> Parque COMFAMA
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-2048">
              <Store className="w-5 h-5 text-brand-yellow" data-id="element-2049" /> 36 Stands
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Stand Selector */}
      <section className="py-20 bg-white" data-id="element-2050">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2051">
          <div className="text-center mb-12" data-id="element-2052">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4" data-id="element-2053">
              {['Selecciona', 'tu', 'Stand'].map((word, i) => <motion.span key={i} initial={{
              opacity: 0,
              y: 30,
              filter: 'blur(10px)'
            }} whileInView={{
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: i * 0.15,
              ease: [0.22, 1, 0.36, 1]
            }} className="inline-block shimmer-text mr-3" data-id="element-2054">
                  {word}
                </motion.span>)}
            </h2>
            <p className="text-gray-600" data-id="element-2055">
              Elige la ubicación perfecta para tu marca en nuestro mapa
              interactivo.
            </p>
          </div>

          {/* Tabs - ONLY 2 NOW */}
          <div className="flex justify-center mb-10" data-id="element-2056">
            <div className="bg-gray-100 p-1.5 rounded-full flex gap-2 max-w-xl w-full" data-id="element-2057">
              <button onClick={() => {
              setActiveTab('comercial');
              setSelectedStand(null);
            }} className={`flex-1 py-3 rounded-full font-bold text-sm md:text-base transition-all ${activeTab === 'comercial' ? 'bg-brand-navy text-white shadow-md' : 'text-gray-500 hover:text-brand-navy'}`} data-id="element-2058">
                Comercial
              </button>
              <button onClick={() => {
              setActiveTab('foodtrucks');
              setSelectedStand(null);
            }} className={`flex-1 py-3 rounded-full font-bold text-sm md:text-base transition-all ${activeTab === 'foodtrucks' ? 'bg-brand-navy text-white shadow-md' : 'text-gray-500 hover:text-brand-navy'}`} data-id="element-2059">
                Food Trucks - Toldos
              </button>
            </div>
          </div>

          {/* Legend - UPDATED */}
          <div className="flex flex-wrap justify-center gap-6 mb-10" data-id="element-2060">
            {!(activeTab === 'foodtrucks' && ftSubTab === 'toldos') && <>
                <div className="flex items-center gap-2" data-id="element-2061">
                  <div className="w-4 h-4 rounded border-2 border-gray-300 bg-white" data-id="element-2062"></div>
                  <span className="text-sm text-gray-600" data-id="element-2063">Disponible</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2064">
                  <div className="w-4 h-4 rounded border-2 border-amber-400 bg-amber-100" data-id="element-2065"></div>
                  <span className="text-sm text-gray-600" data-id="element-2066">Reservado</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2067">
                  <div className="w-4 h-4 rounded border-2 border-red-400 bg-red-100" data-id="element-2068"></div>
                  <span className="text-sm text-gray-600" data-id="element-2069">Vendido</span>
                </div>
              </>}
            {activeTab === 'comercial' && <>
                <div className="flex items-center gap-2" data-id="element-2070">
                  <div className="w-5 h-5 rounded-lg bg-gradient-to-br from-yellow-50 to-amber-50 border-[3px] border-yellow-500" data-id="element-2071"></div>
                  <span className="text-sm text-gray-600 font-semibold" data-id="element-2072">
                    AAA — Premium 3 frentes
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2073">
                  <div className="w-5 h-5 rounded-lg bg-white border-[3px] border-amber-400" data-id="element-2074"></div>
                  <span className="text-sm text-gray-600 font-semibold" data-id="element-2075">
                    AA — Premium 2 frentes
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2076">
                  <div className="w-4 h-4 rounded bg-white border-2 border-gray-300" data-id="element-2077"></div>
                  <span className="text-sm text-gray-600" data-id="element-2078">
                    A — Estándar 1 frente
                  </span>
                </div>
              </>}
            {activeTab === 'foodtrucks' && ftSubTab === 'foodtrucks' && <>
                <div className="flex items-center gap-2" data-id="element-2079">
                  <div className="w-5 h-5 rounded-lg bg-orange-500/30 border-2 border-orange-500/50" data-id="element-2080"></div>
                  <span className="text-sm text-gray-600 font-semibold" data-id="element-2081">
                    Food Truck
                  </span>
                </div>
              </>}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10" data-id="element-2082">
            {/* Grid Map */}
            {(() => {
            const PavilionContent = () => <>
                  {activeTab === 'comercial' ? <div className="min-w-[640px]" data-id="element-2083">
                      {/* Title */}
                      <div className="mb-6 text-center" data-id="element-2084">
                        <h3 className="text-2xl font-bold text-[#5fc9d4] tracking-wider" data-id="element-2085">
                          PABELLÓN COMERCIAL
                        </h3>
                        <p className="text-white/60 text-sm mt-2" data-id="element-2086">
                          36 Stands Disponibles
                        </p>
                      </div>

                      {/* Floor Plan Container */}
                      <div className="relative bg-white/5 p-6 rounded-xl border border-white/10" data-id="element-2087">
                        <div className="flex gap-4" data-id="element-2088">
                          {/* Left SALIDA labels */}
                          <div className="flex flex-col items-center justify-around" data-id="element-2089">
                            <div className="text-white/70 text-xs font-bold" style={{
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)'
                      }} data-id="element-2090">
                              SALIDA
                            </div>
                            <div className="text-white/70 text-xs font-bold" style={{
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)'
                      }} data-id="element-2091">
                              SALIDA
                            </div>
                          </div>

                          <div className="flex-1" data-id="element-2092">
                            {/* Row 1: 01-06 | SALIDA | 07-12 */}
                            <div className="flex gap-1 mb-4 items-center" data-id="element-2093">
                              <div className="grid gap-1 flex-1" style={{
                          gridTemplateColumns: 'repeat(6, 1fr)'
                        }} data-id="element-2094">
                                {commercialStands.filter(s => s.row === 1 && (s.col ?? 0) <= 6).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getCommercialStandClasses(stand)} style={stand.standType === 'AAA' || stand.standType === 'AA' ? {
                            aspectRatio: '1',
                            minHeight: '100%'
                          } : {
                            aspectRatio: '1'
                          }} data-id="element-2095">
                                      <span className={stand.standType === 'AAA' || stand.standType === 'AA' ? 'text-[8px] leading-tight text-center' : 'text-[9px]'} data-id="element-2096">
                                        {stand.id}
                                      </span>
                                      {stand.standType === 'AAA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 text-yellow-500 text-xs" data-id="element-2097">
                                            ★★★
                                          </span>}
                                      {stand.standType === 'AA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border border-amber-600 shadow-sm" data-id="element-2098"></span>}
                                    </button>)}
                              </div>
                              <div className="px-1 text-white/70 text-[8px] font-bold text-center leading-tight" style={{
                          writingMode: 'vertical-rl',
                          transform: 'rotate(180deg)'
                        }} data-id="element-2099">
                                SALIDA
                              </div>
                              <div className="grid gap-1 flex-1" style={{
                          gridTemplateColumns: 'repeat(6, 1fr)'
                        }} data-id="element-2100">
                                {commercialStands.filter(s => s.row === 1 && (s.col ?? 0) > 6).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getCommercialStandClasses(stand)} style={stand.standType === 'AAA' || stand.standType === 'AA' ? {
                            aspectRatio: '1',
                            minHeight: '100%'
                          } : {
                            aspectRatio: '1'
                          }} data-id="element-2101">
                                      <span className={stand.standType === 'AAA' || stand.standType === 'AA' ? 'text-[8px] leading-tight text-center' : 'text-[9px]'} data-id="element-2102">
                                        {stand.id}
                                      </span>
                                      {stand.standType === 'AAA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 text-yellow-500 text-xs" data-id="element-2103">
                                            ★★★
                                          </span>}
                                      {stand.standType === 'AA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border border-amber-600 shadow-sm" data-id="element-2104"></span>}
                                    </button>)}
                              </div>
                            </div>

                            {/* PASO PEATONAL 1 */}
                            <div className="mb-4 py-3 bg-white/10 rounded-lg border border-white/20" data-id="element-2105">
                              <div className="text-center" data-id="element-2106">
                                <div className="text-white font-bold text-sm" data-id="element-2107">
                                  PASO PEATONAL
                                </div>
                              </div>
                            </div>

                            {/* Row 2: 13-24 (central, no SALIDA) */}
                            <div className="grid gap-1 mb-4" style={{
                        gridTemplateColumns: 'repeat(12, 1fr)'
                      }} data-id="element-2108">
                              {commercialStands.filter(s => s.row === 2).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getCommercialStandClasses(stand)} style={stand.standType === 'AAA' || stand.standType === 'AA' ? {
                          aspectRatio: '1',
                          minHeight: '100%'
                        } : {
                          aspectRatio: '1'
                        }} data-id="element-2109">
                                    <span className={stand.standType === 'AAA' || stand.standType === 'AA' ? 'text-[8px] leading-tight text-center' : 'text-[9px]'} data-id="element-2110">
                                      {stand.id}
                                    </span>
                                    {stand.standType === 'AAA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 text-yellow-500 text-xs" data-id="element-2111">
                                          ★★★
                                        </span>}
                                    {stand.standType === 'AA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border border-amber-600 shadow-sm" data-id="element-2112"></span>}
                                  </button>)}
                            </div>

                            {/* PASO PEATONAL 2 */}
                            <div className="mb-4 py-3 bg-white/10 rounded-lg border border-white/20" data-id="element-2113">
                              <div className="text-center" data-id="element-2114">
                                <div className="text-white font-bold text-sm" data-id="element-2115">
                                  PASO PEATONAL
                                </div>
                              </div>
                            </div>

                            {/* Row 3: 25-30 | SALIDA | 31-36 */}
                            <div className="flex gap-1 items-center" data-id="element-2116">
                              <div className="grid gap-1 flex-1" style={{
                          gridTemplateColumns: 'repeat(6, 1fr)'
                        }} data-id="element-2117">
                                {commercialStands.filter(s => s.row === 3 && (s.col ?? 0) <= 30).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getCommercialStandClasses(stand)} style={stand.standType === 'AAA' || stand.standType === 'AA' ? {
                            aspectRatio: '1',
                            minHeight: '100%'
                          } : {
                            aspectRatio: '1'
                          }} data-id="element-2118">
                                      <span className={stand.standType === 'AAA' || stand.standType === 'AA' ? 'text-[8px] leading-tight text-center' : 'text-[9px]'} data-id="element-2119">
                                        {stand.id}
                                      </span>
                                      {stand.standType === 'AAA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 text-yellow-500 text-xs" data-id="element-2120">
                                            ★★★
                                          </span>}
                                      {stand.standType === 'AA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border border-amber-600 shadow-sm" data-id="element-2121"></span>}
                                    </button>)}
                              </div>
                              <div className="px-1 text-white/70 text-[8px] font-bold text-center leading-tight" style={{
                          writingMode: 'vertical-rl',
                          transform: 'rotate(180deg)'
                        }} data-id="element-2122">
                                SALIDA
                              </div>
                              <div className="grid gap-1 flex-1" style={{
                          gridTemplateColumns: 'repeat(6, 1fr)'
                        }} data-id="element-2123">
                                {commercialStands.filter(s => s.row === 3 && (s.col ?? 0) > 30).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getCommercialStandClasses(stand)} style={stand.standType === 'AAA' || stand.standType === 'AA' ? {
                            aspectRatio: '1',
                            minHeight: '100%'
                          } : {
                            aspectRatio: '1'
                          }} data-id="element-2124">
                                      <span className={stand.standType === 'AAA' || stand.standType === 'AA' ? 'text-[8px] leading-tight text-center' : 'text-[9px]'} data-id="element-2125">
                                        {stand.id}
                                      </span>
                                      {stand.standType === 'AAA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 text-yellow-500 text-xs" data-id="element-2126">
                                            ★★★
                                          </span>}
                                      {stand.standType === 'AA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border border-amber-600 shadow-sm" data-id="element-2127"></span>}
                                    </button>)}
                              </div>
                            </div>
                          </div>

                          {/* Right SALIDA */}
                          <div className="flex flex-col justify-center" data-id="element-2128">
                            <div className="text-white/70 text-xs font-bold" style={{
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)'
                      }} data-id="element-2129">
                              SALIDA
                            </div>
                          </div>
                        </div>
                      </div>
                    </div> :
              // Food Trucks & Toldos
              <div className="min-w-[480px]" data-id="element-2130">
                      {/* Sub-tabs */}
                      <div className="flex justify-center mb-8" data-id="element-2131">
                        <div className="bg-white/20 p-1 rounded-full flex gap-1 w-full max-w-sm border border-white/20" data-id="element-2132">
                          <button onClick={() => setFtSubTab('foodtrucks')} className={`flex-1 py-2 rounded-full font-bold text-sm transition-all ${ftSubTab === 'foodtrucks' ? 'bg-orange-500 text-white shadow-md' : 'text-white/90 hover:text-white hover:bg-white/10'}`} data-id="element-2133">
                            Food Trucks
                          </button>
                          <button onClick={() => setFtSubTab('toldos')} className={`flex-1 py-2 rounded-full font-bold text-sm transition-all ${ftSubTab === 'toldos' ? 'bg-amber-500 text-white shadow-md' : 'text-white/90 hover:text-white hover:bg-white/10'}`} data-id="element-2134">
                            Toldos
                          </button>
                        </div>
                      </div>

                      {ftSubTab === 'foodtrucks' ? <div data-id="element-2135">
                          {/* Food Trucks header */}
                          <div className="mb-6 text-center" data-id="element-2136">
                            <h3 className="text-2xl font-bold text-orange-400 tracking-wider" data-id="element-2137">
                              ZONA FOOD TRUCKS
                            </h3>
                            <p className="text-white/60 text-sm mt-2" data-id="element-2138">
                              8 Espacios Disponibles · $150.000/m²
                            </p>
                          </div>
                          <div className="relative bg-white/5 p-6 rounded-xl border border-white/10" data-id="element-2139">
                            {/* Top Row */}
                            <div className="grid grid-cols-4 gap-6 mb-8" data-id="element-2140">
                              {foodTruckStands.filter(s => s.row === 1).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getFoodTruckStandClasses(stand)} data-id="element-2141">
                                    <img src="https://cdn.magicpatterns.com/uploads/nFXcbyhaDyaXddokgBVZZ4/foodtruck.png" alt="Food Truck" className={`w-20 h-auto mb-2 ${stand.status === 'available' ? 'brightness-0 invert' : 'brightness-0 invert opacity-40'}`} data-id="element-2142" />
                                    <span className="font-bold text-sm" data-id="element-2143">
                                      {stand.id}
                                    </span>
                                    {stand.status === 'sold' && <span className="text-[9px] font-bold uppercase tracking-wider mt-0.5" data-id="element-2144">
                                        Vendido
                                      </span>}
                                    {stand.status === 'reserved' && <span className="text-[9px] font-bold uppercase tracking-wider mt-0.5" data-id="element-2145">
                                        Reservado
                                      </span>}
                                  </button>)}
                            </div>
                            {/* Paso peatonal */}
                            <div className="my-6 py-4 bg-white/10 rounded-lg border border-white/20" data-id="element-2146">
                              <div className="text-center" data-id="element-2147">
                                <div className="text-white font-bold text-sm tracking-widest" data-id="element-2148">
                                  ZONA DE CIRCULACIÓN Y MESAS
                                </div>
                              </div>
                            </div>
                            {/* Bottom Row */}
                            <div className="grid grid-cols-4 gap-6 mt-8" data-id="element-2149">
                              {foodTruckStands.filter(s => s.row === 2).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getFoodTruckStandClasses(stand)} data-id="element-2150">
                                    <img src="https://cdn.magicpatterns.com/uploads/nFXcbyhaDyaXddokgBVZZ4/foodtruck.png" alt="Food Truck" className={`w-20 h-auto mb-2 ${stand.status === 'available' ? 'brightness-0 invert' : 'brightness-0 invert opacity-40'}`} data-id="element-2151" />
                                    <span className="font-bold text-sm" data-id="element-2152">
                                      {stand.id}
                                    </span>
                                    {stand.status === 'sold' && <span className="text-[9px] font-bold uppercase tracking-wider mt-0.5" data-id="element-2153">
                                        Vendido
                                      </span>}
                                    {stand.status === 'reserved' && <span className="text-[9px] font-bold uppercase tracking-wider mt-0.5" data-id="element-2154">
                                        Reservado
                                      </span>}
                                  </button>)}
                            </div>
                          </div>
                        </div> :
                // Toldos Gastronómicos Section
                <div className="bg-gradient-to-br from-amber-500/20 to-orange-600/20 p-6 sm:p-8 rounded-2xl border-2 border-amber-500/30 shadow-xl relative overflow-hidden" data-id="element-2155">
                          {/* Decorative background elements */}
                          <div className="absolute top-0 right-0 -mr-10 -mt-10 w-40 h-40 bg-amber-500/20 rounded-full blur-3xl" data-id="element-2156"></div>
                          <div className="absolute bottom-0 left-0 -ml-10 -mb-10 w-40 h-40 bg-orange-500/20 rounded-full blur-3xl" data-id="element-2157"></div>

                          <div className="relative z-10" data-id="element-2158">
                            <div className="flex items-center gap-4 mb-6" data-id="element-2159">
                              <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-3 rounded-xl shadow-lg" data-id="element-2160">
                                <img src="https://cdn.magicpatterns.com/uploads/pBA1b9YRLahkDTRzbjdxcq/toldo.png" alt="Toldo" className="w-8 h-8 brightness-0 invert" data-id="element-2161" />
                              </div>
                              <div data-id="element-2162">
                                <h3 className="text-2xl font-black text-white tracking-wide" data-id="element-2163">
                                  Toldos Gastronómicos
                                </h3>
                                <p className="text-amber-200/80 text-sm font-medium" data-id="element-2164">
                                  Ubicación asignada por zonas según
                                  disponibilidad
                                </p>
                              </div>
                            </div>

                            {/* Urgency counter */}
                            <div className="bg-black/20 backdrop-blur-md rounded-xl p-5 mb-5 border border-white/10" data-id="element-2165">
                              <div className="flex justify-between items-end mb-3" data-id="element-2166">
                                <div data-id="element-2167">
                                  <span className="text-white/80 text-sm font-medium block mb-1" data-id="element-2168">
                                    Disponibilidad
                                  </span>
                                  <span className="text-amber-400 font-black text-2xl" data-id="element-2169">
                                    {toldosAvailable}{' '}
                                    <span className="text-lg text-white/50 font-medium" data-id="element-2170">
                                      de {TOLDOS_TOTAL}
                                    </span>
                                  </span>
                                </div>
                                {toldosAvailable <= 5 && <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-3 py-1 rounded-full text-xs font-bold animate-pulse" data-id="element-2171">
                                    ¡Últimas unidades!
                                  </div>}
                              </div>
                              <div className="w-full bg-black/30 rounded-full h-2.5 overflow-hidden" data-id="element-2172">
                                <div className="bg-gradient-to-r from-amber-500 to-orange-500 h-full rounded-full transition-all duration-500 relative" style={{
                          width: `${toldosAvailable / TOLDOS_TOTAL * 100}%`
                        }} data-id="element-2173">
                                  <div className="absolute inset-0 bg-white/20 w-full h-full animate-[shimmer_2s_infinite]" data-id="element-2174"></div>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6" data-id="element-2175">
                              {/* Price */}
                              <div className="bg-black/20 backdrop-blur-md rounded-xl p-5 border border-white/10 flex flex-col justify-center" data-id="element-2176">
                                <p className="text-white/70 text-sm font-medium mb-1" data-id="element-2177">
                                  Inversión por toldo
                                </p>
                                <p className="text-3xl font-black text-white" data-id="element-2178">
                                  $300.000{' '}
                                  <span className="text-sm font-medium text-white/50" data-id="element-2179">
                                    + IVA
                                  </span>
                                </p>
                              </div>

                              {/* Quantity selector */}
                              <div className="bg-black/20 backdrop-blur-md rounded-xl p-5 border border-white/10" data-id="element-2180">
                                <label className="text-white/90 text-sm font-medium mb-3 block" data-id="element-2181">
                                  Cantidad a reservar
                                </label>
                                <div className="flex items-center justify-between gap-3 bg-black/30 rounded-lg p-1 border border-white/5" data-id="element-2182">
                                  <button onClick={() => setToldosQuantity(Math.max(0, toldosQuantity - 1))} className="w-10 h-10 rounded-md bg-white/5 hover:bg-white/10 text-white font-bold transition-colors flex items-center justify-center disabled:opacity-30" disabled={toldosQuantity <= 0} data-id="element-2183">
                                    −
                                  </button>
                                  <input type="number" min="0" max={toldosAvailable} value={toldosQuantity} onChange={e => {
                            const val = parseInt(e.target.value) || 0;
                            setToldosQuantity(Math.min(Math.max(0, val), toldosAvailable));
                          }} className="w-16 text-center bg-transparent border-none px-2 py-2 text-white font-black text-xl focus:outline-none" data-id="element-2184" />
                                  <button onClick={() => setToldosQuantity(Math.min(toldosAvailable, toldosQuantity + 1))} className="w-10 h-10 rounded-md bg-white/5 hover:bg-white/10 text-white font-bold transition-colors flex items-center justify-center disabled:opacity-30" disabled={toldosQuantity >= toldosAvailable} data-id="element-2185">
                                    +
                                  </button>
                                </div>
                                {toldosQuantity === 0 && <p className="text-amber-300/80 text-xs mt-3 text-center font-medium animate-pulse" data-id="element-2186">
                                    ↑ Elige cuántos toldos reservar
                                  </p>}
                                {toldosQuantity > 0 && <p className="text-white/60 text-xs mt-3 text-center" data-id="element-2187">
                                    Continúa en el panel de la derecha →
                                  </p>}
                              </div>
                            </div>
                          </div>
                        </div>}
                    </div>}
                </>;
            return <>
                  <div className="lg:col-span-2 relative bg-gradient-to-br from-[#0a4a5c] to-[#0d3d4d] p-3 sm:p-6 md:p-10 rounded-3xl border-2 border-[#1a6b7f] overflow-x-auto scroll-smooth shadow-2xl" data-id="element-2188">
                    <p className="sm:hidden text-white/70 text-xs text-center mb-4 animate-pulse" data-id="element-2189">
                      Desliza o toca Ver en grande →
                    </p>
                    <button onClick={() => setIsModalOpen(true)} className="absolute top-4 right-4 z-10 bg-brand-cyan text-white rounded-full px-3 py-2 md:px-4 flex items-center gap-2 shadow-lg hover:bg-blue-600 transition-colors" data-id="element-2190">
                      <Maximize2 className="w-4 h-4" data-id="element-2191" />
                      <span className="hidden md:inline text-sm font-bold" data-id="element-2192">
                        Ver en grande
                      </span>
                    </button>
                    <PavilionContent data-id="element-2193" />
                  </div>

                  {/* Modal */}
                  {isModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/95 backdrop-blur-sm p-4" data-id="element-2194">
                      <motion.div initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.95
                }} className="w-full h-full max-w-6xl max-h-[90vh] bg-gradient-to-br from-[#0a4a5c] to-[#0d3d4d] rounded-3xl border-2 border-[#1a6b7f] shadow-2xl flex flex-col overflow-hidden" data-id="element-2195">
                        {/* Header */}
                        <div className="flex flex-col sm:flex-row items-center justify-between p-4 sm:p-6 border-b border-white/10 gap-4 relative" data-id="element-2196">
                          <h2 className="text-xl sm:text-2xl font-bold text-white pr-10 sm:pr-0" data-id="element-2197">
                            {activeTab === 'comercial' ? 'Pabellón Comercial' : 'Food Trucks - Toldos'}
                          </h2>

                          <div className="flex-1 flex justify-center w-full sm:w-auto" data-id="element-2198">
                            <div className="bg-white/10 p-1 rounded-full flex gap-1 w-full sm:w-auto" data-id="element-2199">
                              <button onClick={() => {
                          setActiveTab('comercial');
                          setSelectedStand(null);
                        }} className={`px-4 py-2 rounded-full font-bold text-xs sm:text-sm whitespace-nowrap transition-all ${activeTab === 'comercial' ? 'bg-brand-cyan text-white' : 'text-white/60 hover:text-white'}`} data-id="element-2200">
                                Comercial
                              </button>
                              <button onClick={() => {
                          setActiveTab('foodtrucks');
                          setSelectedStand(null);
                        }} className={`px-4 py-2 rounded-full font-bold text-xs sm:text-sm whitespace-nowrap transition-all ${activeTab === 'foodtrucks' ? 'bg-brand-cyan text-white' : 'text-white/60 hover:text-white'}`} data-id="element-2201">
                                Food Trucks - Toldos
                              </button>
                            </div>
                          </div>

                          <button onClick={() => setIsModalOpen(false)} className="absolute top-4 right-4 sm:relative sm:top-0 sm:right-0 bg-white/10 hover:bg-red-500 text-white p-2 rounded-full transition-colors" data-id="element-2202">
                            <X className="w-6 h-6" data-id="element-2203" />
                          </button>
                        </div>

                        {/* Body with Zoom */}
                        <div className="flex-1 overflow-hidden relative bg-[#083846] cursor-move" data-id="element-2204">
                          <TransformWrapper initialScale={1} minScale={0.5} maxScale={4} centerOnInit={true} wheel={{
                      step: 0.1
                    }} data-id="element-2205">
                            <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full !h-full flex items-center justify-center p-8" data-id="element-2206">
                              <div className="bg-gradient-to-br from-[#0a4a5c] to-[#0d3d4d] p-8 rounded-3xl border border-[#1a6b7f] shadow-2xl" data-id="element-2207">
                                <PavilionContent data-id="element-2208" />
                              </div>
                            </TransformComponent>
                          </TransformWrapper>
                        </div>
                      </motion.div>
                    </div>}
                </>;
          })()}

            {/* Detail Panel */}
            <div className="lg:col-span-1" data-id="element-2209">
              <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 sticky top-28" data-id="element-2210">
                {/* Toldos summary panel: takes priority on toldos sub-tab when quantity > 0 */}
                {activeTab === 'foodtrucks' && ftSubTab === 'toldos' && toldosQuantity > 0 ? <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} key={`toldos-${toldosQuantity}`} data-id="element-2211">
                    <div className="mb-6" data-id="element-2212">
                      <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold mb-3 uppercase tracking-wider" data-id="element-2213">
                        Listo para reservar
                      </span>
                      <h3 className="text-3xl font-bold text-brand-navy" data-id="element-2214">
                        {toldosQuantity} Toldo{toldosQuantity > 1 ? 's' : ''}{' '}
                        Gastronómico{toldosQuantity > 1 ? 's' : ''}
                      </h3>
                      <p className="text-gray-500 font-semibold mt-1" data-id="element-2215">
                        Ubicación asignada por zonas
                      </p>
                    </div>

                    <div className="space-y-2 mb-6 bg-amber-50/60 rounded-xl p-4 border border-amber-100" data-id="element-2216">
                      <div className="flex justify-between text-sm" data-id="element-2217">
                        <span className="text-gray-500" data-id="element-2218">Cantidad</span>
                        <span className="font-bold text-brand-navy" data-id="element-2219">
                          {toldosQuantity} ×
                        </span>
                      </div>
                      <div className="flex justify-between text-sm" data-id="element-2220">
                        <span className="text-gray-500" data-id="element-2221">Precio por toldo</span>
                        <span className="font-bold text-brand-navy" data-id="element-2222">
                          {formatCOP(toldosPrice)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm" data-id="element-2223">
                        <span className="text-gray-500" data-id="element-2224">Subtotal</span>
                        <span className="font-medium text-brand-navy" data-id="element-2225">
                          {formatCOP(toldosTotalPrice)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm" data-id="element-2226">
                        <span className="text-gray-500" data-id="element-2227">IVA (19%)</span>
                        <span className="font-medium text-brand-navy" data-id="element-2228">
                          {formatCOP(priceBreakdown(toldosTotalPrice).iva)}
                        </span>
                      </div>
                      <div className="border-t border-amber-200 pt-2 flex justify-between items-end" data-id="element-2229">
                        <span className="text-gray-700 font-bold" data-id="element-2230">
                          Total a pagar
                        </span>
                        <span className="font-black text-2xl text-orange-600" data-id="element-2231">
                          {formatCOP(priceBreakdown(toldosTotalPrice).total)}
                        </span>
                      </div>
                    </div>

                    <div className="mb-8" data-id="element-2232">
                      <h4 className="font-bold text-brand-navy mb-3 text-sm uppercase tracking-wider" data-id="element-2233">
                        Cada toldo incluye:
                      </h4>
                      <ul className="space-y-2" data-id="element-2234">
                        {['1 Toldo de feria + 1 mesa + 1 silla', '1 conexión 110V (TV / PC / celular)', '2 credenciales de expositor', 'Lleva tu propia exhibición'].map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-600" data-id="element-2235">
                            <Check className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" data-id="element-2236" />
                            {item}
                          </li>)}
                      </ul>
                    </div>

                    <button onClick={() => {
                  setRegistrationType('toldo');
                  setIsRegistrationModalOpen(true);
                }} className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white py-4 rounded-xl font-bold transition-all shadow-lg hover:shadow-orange-500/30" data-id="element-2237">
                      Reservar {toldosQuantity} toldo
                      {toldosQuantity > 1 ? 's' : ''}
                      <ChevronRight className="w-5 h-5" data-id="element-2238" />
                    </button>
                  </motion.div> : selectedStand ? <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} key={selectedStand.id} data-id="element-2239">
                    <div className="flex justify-between items-start mb-6" data-id="element-2240">
                      <div data-id="element-2241">
                        <span className="inline-block px-3 py-1 bg-brand-green/10 text-brand-green rounded-full text-xs font-bold mb-3 uppercase tracking-wider" data-id="element-2242">
                          Disponible
                        </span>
                        {selectedStand.standType === 'AAA' && <span className="inline-block px-3 py-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-900 rounded-full text-xs font-bold mb-3 uppercase tracking-wider ml-2" data-id="element-2243">
                            ★★★ SUPER PREMIUM
                          </span>}
                        {selectedStand.standType === 'AA' && <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold mb-3 uppercase tracking-wider ml-2" data-id="element-2244">
                            PREMIUM
                          </span>}
                        <h3 className="text-3xl font-bold text-brand-navy" data-id="element-2245">
                          {selectedStand.type === 'foodtrucks' ? 'Food Truck' : 'Stand'}{' '}
                          {selectedStand.id}
                        </h3>
                        <p className="text-gray-500 font-semibold mt-1" data-id="element-2246">
                          {selectedStand.type === 'foodtrucks' ? 'Zona Food Trucks' : 'Pabellón Comercial'}
                        </p>
                      </div>
                    </div>

                    {selectedStand.type === 'foodtrucks' ? <div className="space-y-5 mb-8" data-id="element-2247">
                        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4" data-id="element-2248">
                          <p className="text-sm font-bold text-orange-700 mb-1" data-id="element-2249">
                            Precio por m²
                          </p>
                          <p className="text-2xl font-bold text-orange-600" data-id="element-2250">
                            $150.000
                          </p>
                        </div>

                        <div data-id="element-2251">
                          <p className="text-sm font-bold text-brand-navy mb-3" data-id="element-2252">
                            Ingresa las medidas de tu Food Truck:
                          </p>
                          <div className="flex gap-3 items-center" data-id="element-2253">
                            <div className="flex-1" data-id="element-2254">
                              <label className="text-xs text-gray-500 mb-1 block" data-id="element-2255">
                                Ancho (m)
                              </label>
                              <input type="number" min={1} max={10} step={0.5} value={ftWidthStr} onChange={e => setFtWidthStr(e.target.value)} onBlur={() => {
                          const v = parseFloat(ftWidthStr);
                          if (!v || v < 1) setFtWidthStr('1');else if (v > 10) setFtWidthStr('10');
                        }} className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-center font-bold text-brand-navy focus:border-orange-400 focus:outline-none transition-colors" data-id="element-2256" />
                            </div>
                            <span className="text-gray-400 font-bold text-lg mt-5" data-id="element-2257">
                              ×
                            </span>
                            <div className="flex-1" data-id="element-2258">
                              <label className="text-xs text-gray-500 mb-1 block" data-id="element-2259">
                                Largo (m)
                              </label>
                              <input type="number" min={1} max={10} step={0.5} value={ftLengthStr} onChange={e => setFtLengthStr(e.target.value)} onBlur={() => {
                          const v = parseFloat(ftLengthStr);
                          if (!v || v < 1) setFtLengthStr('1');else if (v > 10) setFtLengthStr('10');
                        }} className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-center font-bold text-brand-navy focus:border-orange-400 focus:outline-none transition-colors" data-id="element-2260" />
                            </div>
                          </div>
                        </div>

                        <div className="bg-brand-navy/5 rounded-xl p-4 space-y-2" data-id="element-2261">
                          <div className="flex justify-between text-sm" data-id="element-2262">
                            <span className="text-gray-500" data-id="element-2263">Área total</span>
                            <span className="font-bold text-brand-navy" data-id="element-2264">
                              {ftTotalM2.toFixed(2)} m²
                            </span>
                          </div>
                          <div className="flex justify-between text-sm" data-id="element-2265">
                            <span className="text-gray-500" data-id="element-2266">Precio × m²</span>
                            <span className="font-bold text-brand-navy" data-id="element-2267">
                              {formatCOP(ftPricePerM2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm" data-id="element-2268">
                            <span className="text-gray-500" data-id="element-2269">Subtotal</span>
                            <span className="font-medium text-brand-navy" data-id="element-2270">
                              {formatCOP(ftTotalPrice)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm" data-id="element-2271">
                            <span className="text-gray-500" data-id="element-2272">IVA (19%)</span>
                            <span className="font-medium text-brand-navy" data-id="element-2273">
                              {formatCOP(priceBreakdown(ftTotalPrice).iva)}
                            </span>
                          </div>
                          <div className="border-t border-gray-200 pt-2 flex justify-between" data-id="element-2274">
                            <span className="text-gray-700 font-bold" data-id="element-2275">
                              Total a pagar
                            </span>
                            <span className="font-bold text-xl text-orange-600" data-id="element-2276">
                              {formatCOP(priceBreakdown(ftTotalPrice).total)}
                            </span>
                          </div>
                        </div>
                      </div> : <div className="space-y-4 mb-8" data-id="element-2277">
                        <div className="flex justify-between py-3 border-b border-gray-100" data-id="element-2278">
                          <span className="text-gray-500" data-id="element-2279">Dimensiones</span>
                          <span className="font-bold text-brand-navy" data-id="element-2280">
                            {selectedStand.size}
                          </span>
                        </div>
                        <div className="flex justify-between py-3 border-b border-gray-100" data-id="element-2281">
                          <span className="text-gray-500" data-id="element-2282">Frentes</span>
                          <span className="font-bold text-brand-navy" data-id="element-2283">
                            {selectedStand.frentes}
                          </span>
                        </div>
                        <div className="flex justify-between py-3 border-b border-gray-100" data-id="element-2284">
                          <span className="text-gray-500" data-id="element-2285">Inversión</span>
                          <span className="font-medium text-brand-navy" data-id="element-2286">
                            {formatCOP(selectedStand.price)}
                          </span>
                        </div>
                        <div className="flex justify-between py-2" data-id="element-2287">
                          <span className="text-gray-500 text-sm" data-id="element-2288">
                            IVA (19%)
                          </span>
                          <span className="font-medium text-brand-navy text-sm" data-id="element-2289">
                            {formatCOP(priceBreakdown(selectedStand.price).iva)}
                          </span>
                        </div>
                        <div className="flex justify-between py-2 border-t border-gray-200" data-id="element-2290">
                          <span className="text-brand-navy font-bold" data-id="element-2291">
                            Total a pagar
                          </span>
                          <span className="font-black text-xl text-brand-navy" data-id="element-2292">
                            {formatCOP(priceBreakdown(selectedStand.price).total)}
                          </span>
                        </div>
                      </div>}

                    <div className="mb-8" data-id="element-2293">
                      <h4 className="font-bold text-brand-navy mb-3 text-sm uppercase tracking-wider" data-id="element-2294">
                        Incluye:
                      </h4>
                      <ul className="space-y-2" data-id="element-2295">
                        {(selectedStand.type === 'foodtrucks' ? ['Espacio para food truck', 'Energía de planta incluida', '2 credenciales de expositor', 'NO incluye punto de agua'] : ['1 conexión 110V (1 TV / 1 computador / 1 celular)', '2 credenciales de expositor', 'Estructura dentro de la carpa', 'Lleva tu propia exhibición (mesas y sillas adicionales bajo solicitud)', 'Ubicación según disponibilidad']).map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-600" data-id="element-2296">
                            <Check className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" data-id="element-2297" />
                            {item}
                          </li>)}
                      </ul>
                    </div>

                    <button onClick={() => {
                  setRegistrationType(selectedStand.type === 'foodtrucks' ? 'foodtruck' : 'comercial');
                  setIsRegistrationModalOpen(true);
                }} className="w-full flex items-center justify-center gap-2 bg-brand-cyan hover:bg-blue-700 text-white py-4 rounded-xl font-bold transition-all shadow-lg hover:shadow-cyan-500/30" data-id="element-2298">
                      Reservar Espacio
                    </button>
                  </motion.div> : <div className="h-full flex flex-col items-center justify-center text-center py-12 opacity-60" data-id="element-2299">
                    <Info className="w-16 h-16 text-gray-400 mb-4" data-id="element-2300" />
                    <h3 className="text-xl font-bold text-brand-navy mb-2" data-id="element-2301">
                      {activeTab === 'foodtrucks' && ftSubTab === 'toldos' ? 'Selecciona la cantidad' : activeTab === 'foodtrucks' ? 'Ningún espacio seleccionado' : 'Ningún stand seleccionado'}
                    </h3>
                    <p className="text-gray-500" data-id="element-2302">
                      {activeTab === 'foodtrucks' && ftSubTab === 'toldos' ? 'Elige cuántos toldos quieres reservar a la izquierda y aquí verás el resumen.' : `Haz clic en un ${activeTab === 'foodtrucks' ? 'espacio' : 'stand'} disponible en el mapa para ver sus detalles y reservarlo.`}
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Exhibit Section */}
      <section className="py-20 bg-white" data-id="element-2303">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2304">
          <div className="text-center mb-16" data-id="element-2305">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4" data-id="element-2306">
              ¿Por qué ser expositor?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-2307">
              Únete a la feria más importante de la región y haz crecer tu
              negocio en un ambiente lleno de energía y propósito.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-2308">
            {[{
            icon: Users,
            title: 'Gran Audiencia',
            desc: 'Más de 1,000 visitantes esperados durante todo el día del evento.',
            color: 'text-brand-cyan',
            bg: 'bg-brand-cyan/10'
          }, {
            icon: Store,
            title: 'Visibilidad',
            desc: 'Posicionamiento de marca en un entorno premium y familiar.',
            color: 'text-brand-yellow',
            bg: 'bg-brand-yellow/10'
          }, {
            icon: MessageSquare,
            title: 'Networking',
            desc: 'Conecta con otras marcas líderes del sector bienestar y mascotas.',
            color: 'text-brand-green',
            bg: 'bg-brand-green/10'
          }, {
            icon: TrendingUp,
            title: 'Ventas Directas',
            desc: 'Comercializa tus productos y servicios directamente a tu público objetivo.',
            color: 'text-brand-navy',
            bg: 'bg-brand-navy/10'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow text-center" data-id="element-2309">
                <div className={`w-16 h-16 ${item.bg} rounded-2xl flex items-center justify-center mx-auto mb-6`} data-id="element-2310">
                  <item.icon className={`w-8 h-8 ${item.color}`} data-id="element-2311" />
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-2312">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-2313">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pavilion Info Section - UPDATE: REMOVE GASTRONOMICO, KEEP ONLY 2 CARDS */}
      <section className="py-20 bg-gray-50" data-id="element-2314">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2315">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-2316">
            {/* Comercial */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100" data-id="element-2317">
              <div className="flex items-center gap-4 mb-6" data-id="element-2318">
                <div className="w-14 h-14 bg-brand-cyan/10 rounded-2xl flex items-center justify-center" data-id="element-2319">
                  <ShoppingBag className="w-7 h-7 text-brand-cyan" data-id="element-2320" />
                </div>
                <div data-id="element-2321">
                  <h3 className="text-2xl font-bold text-brand-navy" data-id="element-2322">
                    Pabellón Comercial
                  </h3>
                  <p className="text-brand-cyan font-medium" data-id="element-2323">
                    36 Stands Disponibles
                  </p>
                </div>
              </div>
              <p className="text-gray-600 mb-8" data-id="element-2324">
                Ideal para marcas de ropa deportiva, accesorios para mascotas,
                suplementos, tecnología y servicios de bienestar. Ubicado en la
                zona de mayor tráfico peatonal.
              </p>
              <div className="space-y-4" data-id="element-2325">
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-2326">
                  <span className="text-gray-500" data-id="element-2327">
                    Stand A (1 frente, 2x2m)
                  </span>
                  <span className="font-bold text-brand-navy" data-id="element-2328">$500.000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-2329">
                  <span className="text-gray-500" data-id="element-2330">
                    Stand AA (2 frentes, 2x2m)
                  </span>
                  <span className="font-bold text-brand-navy" data-id="element-2331">$600.000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-2332">
                  <span className="text-gray-500" data-id="element-2333">
                    Stand AAA (3 frentes, 2x2m)
                  </span>
                  <span className="font-bold text-brand-navy" data-id="element-2334">$800.000</span>
                </div>
              </div>
            </motion.div>

            {/* Food Trucks & Toldos */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100" data-id="element-2335">
              <div className="flex items-center gap-4 mb-6" data-id="element-2336">
                <div className="w-14 h-14 bg-orange-500/10 rounded-2xl flex items-center justify-center" data-id="element-2337">
                  <Truck className="w-7 h-7 text-orange-500" data-id="element-2338" />
                </div>
                <div data-id="element-2339">
                  <h3 className="text-2xl font-bold text-brand-navy" data-id="element-2340">
                    Food Trucks & Toldos
                  </h3>
                  <p className="text-orange-500 font-medium" data-id="element-2341">
                    8 Food Trucks + 20 Toldos
                  </p>
                </div>
              </div>
              <p className="text-gray-600 mb-8" data-id="element-2342">
                Espacios diseñados para food trucks y toldos gastronómicos.
                Excelente ubicación cerca a la tarima principal y zonas de alto
                tráfico.
              </p>
              <div className="space-y-4" data-id="element-2343">
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-2344">
                  <span className="text-gray-500" data-id="element-2345">Food Truck (variable)</span>
                  <span className="font-bold text-brand-navy" data-id="element-2346">$150.000/m²</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-2347">
                  <span className="text-gray-500" data-id="element-2348">Toldo Gastronómico</span>
                  <span className="font-bold text-brand-navy" data-id="element-2349">$300.000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-2350">
                  <span className="text-gray-500" data-id="element-2351">Energía de planta</span>
                  <span className="font-bold text-brand-green" data-id="element-2352">Incluido</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Summary - UPDATE: REMOVE GASTRONOMICO, UPDATE PRICES */}
      <section className="py-24 bg-brand-navy relative overflow-hidden" data-id="element-2353">
        <div className="absolute inset-0 bg-paw-pattern-white opacity-5 pointer-events-none" data-id="element-2354"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2355">
          <div className="text-center mb-16" data-id="element-2356">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2357">
              Resumen de Inversión
            </h2>
            <p className="text-white/70" data-id="element-2358">
              Opciones adaptadas a las necesidades de tu marca
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8" data-id="element-2359">
            {/* Stand A */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all" data-id="element-2360">
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-2361">Stand A</h3>
              <p className="text-gray-300 mb-6" data-id="element-2362">2×2m · 1 frente</p>
              <div className="mb-8" data-id="element-2363">
                <span className="text-4xl font-black text-white" data-id="element-2364">$500.000</span>
                <span className="text-white/50 text-sm" data-id="element-2365"> + IVA</span>
              </div>
              <ul className="space-y-3 mb-8" data-id="element-2366">
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2367">
                  <Check className="w-4 h-4 text-brand-cyan" data-id="element-2368" /> Dentro de carpa
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2369">
                  <Check className="w-4 h-4 text-brand-cyan" data-id="element-2370" /> 1 Conexión 110V
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2371">
                  <Check className="w-4 h-4 text-brand-cyan" data-id="element-2372" /> 2 Credenciales
                </li>
              </ul>
            </motion.div>

            {/* Stand AA */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-amber-500/20 backdrop-blur-sm rounded-3xl p-8 border-2 border-amber-400" data-id="element-2373">
              <div className="absolute top-0 right-0 bg-amber-400 text-amber-900 px-4 py-1 rounded-bl-xl text-xs font-bold" data-id="element-2374">
                PREMIUM
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-2375">Stand AA</h3>
              <p className="text-amber-300 mb-6" data-id="element-2376">2×2m · 2 frentes</p>
              <div className="mb-8" data-id="element-2377">
                <span className="text-4xl font-black text-white" data-id="element-2378">$600.000</span>
                <span className="text-white/50 text-sm" data-id="element-2379"> + IVA</span>
              </div>
              <ul className="space-y-3 mb-8" data-id="element-2380">
                <li className="flex items-center gap-2 text-white/90 text-sm" data-id="element-2381">
                  <Check className="w-4 h-4 text-amber-400" data-id="element-2382" /> Ubicación esquina
                </li>
                <li className="flex items-center gap-2 text-white/90 text-sm" data-id="element-2383">
                  <Check className="w-4 h-4 text-amber-400" data-id="element-2384" /> Doble frente
                </li>
                <li className="flex items-center gap-2 text-white/90 text-sm" data-id="element-2385">
                  <Check className="w-4 h-4 text-amber-400" data-id="element-2386" /> 2 Credenciales
                </li>
              </ul>
            </motion.div>

            {/* Stand AAA */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-gradient-to-br from-yellow-500/30 to-amber-500/30 backdrop-blur-sm rounded-3xl p-8 border-2 border-yellow-500 relative" data-id="element-2387">
              <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-900 px-4 py-1 rounded-bl-xl text-xs font-bold" data-id="element-2388">
                ★★★ SUPER PREMIUM
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-2389">Stand AAA</h3>
              <p className="text-yellow-300 mb-6" data-id="element-2390">2×2m · 3 frentes</p>
              <div className="mb-8" data-id="element-2391">
                <span className="text-4xl font-black text-white" data-id="element-2392">$800.000</span>
                <span className="text-white/50 text-sm" data-id="element-2393"> + IVA</span>
              </div>
              <ul className="space-y-3 mb-8" data-id="element-2394">
                <li className="flex items-center gap-2 text-white/90 text-sm" data-id="element-2395">
                  <Check className="w-4 h-4 text-yellow-400" data-id="element-2396" /> Ubicación
                  central
                </li>
                <li className="flex items-center gap-2 text-white/90 text-sm" data-id="element-2397">
                  <Check className="w-4 h-4 text-yellow-400" data-id="element-2398" /> Triple frente
                </li>
                <li className="flex items-center gap-2 text-white/90 text-sm" data-id="element-2399">
                  <Check className="w-4 h-4 text-yellow-400" data-id="element-2400" /> Máxima
                  visibilidad
                </li>
              </ul>
            </motion.div>

            {/* Food Trucks */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all" data-id="element-2401">
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-2402">Food Trucks</h3>
              <p className="text-orange-400 mb-6" data-id="element-2403">Variable</p>
              <div className="mb-8" data-id="element-2404">
                <span className="text-4xl font-black text-white" data-id="element-2405">$150.000</span>
                <span className="text-white/50 text-sm" data-id="element-2406"> /m² + IVA</span>
              </div>
              <ul className="space-y-3 mb-8" data-id="element-2407">
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2408">
                  <Check className="w-4 h-4 text-orange-400" data-id="element-2409" /> Espacio para
                  vehículo
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2410">
                  <Check className="w-4 h-4 text-orange-400" data-id="element-2411" /> Energía de
                  planta
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2412">
                  <Check className="w-4 h-4 text-orange-400" data-id="element-2413" /> 2 Credenciales
                </li>
              </ul>
            </motion.div>

            {/* Toldos */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all" data-id="element-2414">
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-2415">Toldos</h3>
              <p className="text-brand-green mb-6" data-id="element-2416">Gastronómico</p>
              <div className="mb-8" data-id="element-2417">
                <span className="text-4xl font-black text-white" data-id="element-2418">$300.000</span>
                <span className="text-white/50 text-sm" data-id="element-2419"> + IVA</span>
              </div>
              <ul className="space-y-3 mb-8" data-id="element-2420">
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2421">
                  <Check className="w-4 h-4 text-brand-green" data-id="element-2422" /> Toldo + mesa +
                  silla
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2423">
                  <Check className="w-4 h-4 text-brand-green" data-id="element-2424" /> 1 Conexión 110V
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2425">
                  <Check className="w-4 h-4 text-brand-green" data-id="element-2426" /> 2 Credenciales
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gray-50" data-id="element-2427">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2428">
          <h2 className="text-3xl font-bold text-brand-navy mb-4" data-id="element-2429">
            ¿Necesitas un montaje especial?
          </h2>
          <p className="text-gray-600 mb-10" data-id="element-2430">
            Si tu marca requiere un espacio mayor a 16m² o un diseño
            arquitectónico personalizado (Free Design), nuestro equipo comercial
            está listo para asesorarte.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2431">
            <a href="https://wa.me/573000000000" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-green-600 text-white px-8 py-4 rounded-full font-bold transition-colors shadow-lg" data-id="element-2432">
              <MessageSquare className="w-5 h-5" data-id="element-2433" /> Contactar por WhatsApp
            </a>
            <a href="mailto:comercial@latidoyhuella.com" className="inline-flex items-center justify-center gap-2 bg-white border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white px-8 py-4 rounded-full font-bold transition-colors" data-id="element-2434">
              <Mail className="w-5 h-5" data-id="element-2435" /> Enviar Correo
            </a>
          </div>
        </div>
      </section>

      <ExpositorRegistrationModal open={isRegistrationModalOpen} onClose={() => setIsRegistrationModalOpen(false)} type={registrationType} selectedStand={selectedStand} price={registrationType === 'toldo' ? toldosPrice : selectedStand?.type === 'foodtrucks' ? ftPricePerM2 : selectedStand?.price || 0} quantity={toldosQuantity} ftDimensions={{
      width: ftWidth,
      length: ftLength,
      totalM2: ftTotalM2
    }} data-id="element-2436" />
    </div>;
}
// Helper icon for corner stands
function StarIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2437">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" data-id="element-2438" />
    </svg>;
}